import { OPERATORS } from '../constants';
import type { OperatorStrategy } from './operator-strategy';

/**
 * The cancellation policy, as the tooltip beside a sensor name states it.
 *
 * This existed three times — TaskcostComponent, TaskcostSensorPreviewComponent
 * and MyTaskComponent each carried a private `selections()` with the same four
 * operator cases pasted into it. The copies had already drifted: only the
 * taskcost one knew about Voyage Radar, so the same ⓘ icon on the other two
 * screens opened an empty tooltip. One place, so the next operator with a prose
 * policy is written once.
 *
 * Two halves, because the callers differ on where the table comes from — the
 * tasking screens read `row.cancelPolicy` off the sensor row, My Tasks looks it
 * up in a separately fetched list keyed by operator and sensor:
 *
 *   1. `cancellationPolicySentence` — the operator's policy in prose, or null.
 *   2. `formatCancellationPolicyEntries` — the document's window/penalty table.
 *
 * A caller takes the sentence when there is one and formats the table when
 * there is not.
 */

/**
 * Operators whose policy is a sentence rather than a table, and that do NOT yet
 * have a strategy of their own. WeatherEx and Voyage Radar are deliberately
 * absent: theirs live in their strategies, which is where these two belong once
 * anyone migrates them.
 */
const CLYDE_SUBSCRIPTION_NOTICE =
  "The sensor can be canceled anytime after two months with one month's" +
  '\n' +
  'notice on the minimum subscription of three months. Similarly,' +
  '\n' +
  "all subscriptions require one month's notice.";

const SPIRE_NO_CANCEL_AFTER_REFRESH = 'No cancelation is permitted once refresh is ordered';

/**
 * The operator's policy in prose, or null when the document's own table is the
 * policy and the caller should render that instead.
 *
 * `operatorKey` is passed separately rather than read off the row: the tasking
 * screens hold it as `row.key` and My Tasks as `element.operatorKey`, and this
 * function has no business knowing which shape it was handed.
 */
export function cancellationPolicySentence(
  operatorKey: string | undefined | null,
  strategy: OperatorStrategy | null | undefined,
  sensorRow?: any,
): string | null {
  // The strategy first, so a migrated operator states its own policy and this
  // file never grows a branch for it.
  const fromStrategy = strategy?.getCancellationPolicyText?.(sensorRow);
  if (fromStrategy) return fromStrategy;

  if (operatorKey === OPERATORS.SPIRE) return SPIRE_NO_CANCEL_AFTER_REFRESH;
  if (operatorKey === OPERATORS.CLYDE) return CLYDE_SUBSCRIPTION_NOTICE;

  return null;
}

/**
 * The operator document's own policy table, one line per entry.
 *
 * Returns '' for an empty or absent table, which is what an operator with no
 * table has always rendered — the caller decides whether that is acceptable, and
 * for the operators where it is not, `cancellationPolicySentence` answers first.
 * Trailing newline per line, exactly as the loops this replaces produced.
 */
export function formatCancellationPolicyEntries(entries: any): string {
  const list = Array.isArray(entries) ? entries : [];
  return list.map((entry) => `Cancelation ${entry?.key} : ${entry?.value}\n`).join('');
}
